import { Skeleton } from '@shared/components/ui/skeleton';
import { cn } from '@shared/lib/utils';

interface SkeletonTableProps {
  rows?: number;
  cols?: number;
  className?: string;
}

/**
 * Skeleton placeholder for table-style loading states.
 * Drop-in replacement for a real table while data is loading.
 */
export function SkeletonTable({ rows = 5, cols = 6, className }: SkeletonTableProps) {
  return (
    <div className={cn('overflow-hidden rounded-xl border bg-card shadow-sm', className)}>
      {/* Header */}
      <div className="flex items-center gap-4 border-b bg-muted/40 px-5 py-3.5">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton
            key={i}
            className={cn('h-4 rounded', i === 0 ? 'w-40' : i === cols - 1 ? 'w-8 ml-auto' : 'w-24')}
          />
        ))}
      </div>

      {/* Rows */}
      <div className="divide-y">
        {Array.from({ length: rows }).map((_, ri) => (
          <div key={ri} className="flex items-center gap-4 px-5 py-4">
            {/* Avatar + text block (first col) */}
            <div className="flex items-center gap-3 w-40 shrink-0">
              <Skeleton className="h-9 w-9 rounded-lg shrink-0" />
              <div className="space-y-1.5 flex-1">
                <Skeleton className="h-3.5 w-28 rounded" />
                <Skeleton className="h-2.5 w-20 rounded" />
              </div>
            </div>
            {/* Middle cols */}
            {Array.from({ length: cols - 2 }).map((_, ci) => (
              <Skeleton
                key={ci}
                className={cn(
                  'h-3.5 rounded',
                  ci === 0 ? 'w-24' : ci === 1 ? 'w-16' : 'w-20',
                )}
              />
            ))}
            {/* Actions col */}
            <Skeleton className="h-7 w-7 rounded ml-auto" />
          </div>
        ))}
      </div>
    </div>
  );
}
